import type { ReluxDraft } from './types'

type Check = { points: number; ok: boolean; hint: string }

const has = (s: string | undefined) => !!s && s.trim() !== ''

/**
 * Completeness score (0-100) shown while the rep fills the form. It is a coaching signal, not a
 * gate: only the minimum fields block submit. `hint` is the most valuable thing still missing.
 */
export function completeness(d: ReluxDraft, photoCount: number) {
  if (d.type === 'amendment') {
    const ok = has(d.changeSummary)
    return { score: ok ? 100 : 40, hint: ok ? null : 'Say what needs to change' }
  }
  const spaces = d.spaces
  const docs = d.documents
  const checks: Check[] = [
    { points: 10, ok: has(d.project.name), hint: 'Add the project name' },
    {
      points: 5,
      ok: has(d.project.siteSuburb) || has(d.project.siteAddress),
      hint: 'Add the site location',
    },
    {
      points: 5,
      ok: has(d.project.application) && has(d.project.buildingType),
      hint: 'Pick the application and building type',
    },
    {
      points: 5,
      ok: has(d.project.projectType) && has(d.project.projectStage),
      hint: 'Pick the project type and stage',
    },
    {
      points: 10,
      ok: d.contacts.some((c) => has(c.fullName)),
      hint: 'Add a contact so the designer knows who to ask',
    },
    {
      points: 15,
      ok: docs.some(
        (x) => x.category === 'Floor Plan' || x.category === 'CAD' || x.category === 'Drawing',
      ),
      hint: 'The designer can start faster with the floor plan',
    },
    {
      points: 20,
      ok:
        spaces.length > 0 &&
        spaces.every(
          (s) => s.shape === 'irregular' || (has(s.lengthM) && has(s.widthM) && has(s.heightM)),
        ),
      hint: 'Add the size of each space (length, width and height)',
    },
    { points: 10, ok: photoCount > 0, hint: 'Add a few photos of the site' },
    {
      points: 5,
      ok: spaces.length > 0 && spaces.every((s) => has(s.targetLux) || s.luxUndecided),
      hint: 'Set the target lux, or tick "Not sure"',
    },
    {
      points: 5,
      ok: spaces.length > 0 && spaces.every((s) => s.environment.length > 0),
      hint: 'Say what the environment is like (dusty, wet...)',
    },
    {
      points: 5,
      ok: spaces.length > 0 && spaces.every((s) => has(s.ceilingType)),
      hint: 'Pick the ceiling type',
    },
    {
      points: 5,
      ok: d.products.mode === 'cam_choose' || d.products.items.length > 0,
      hint: 'Add the products you have in mind',
    },
  ]
  if (d.isQuick) {
    // Quick Relux: name, application, photos and notes are what matter.
    const quick: Check[] = [
      { points: 30, ok: has(d.project.name), hint: 'Add the project name' },
      { points: 20, ok: has(d.project.application), hint: 'Pick the application' },
      { points: 30, ok: photoCount > 0, hint: 'Add a few photos of the site' },
      { points: 20, ok: has(d.notes), hint: 'Add a note or voice note for the designer' },
    ]
    return score(quick)
  }
  return score(checks)
}

function score(checks: Check[]) {
  const total = checks.reduce((n, c) => n + c.points, 0)
  const got = checks.reduce((n, c) => n + (c.ok ? c.points : 0), 0)
  const missing = checks.filter((c) => !c.ok).sort((a, b) => b.points - a.points)[0]
  return { score: Math.round((got / total) * 100), hint: missing?.hint ?? null }
}

/** The minimum needed to submit. Returns plain-language problems, empty when ready. */
export function minimumProblems(d: ReluxDraft): string[] {
  const p: string[] = []
  if (d.type === 'amendment') {
    if (!has(d.changeSummary)) p.push('Say what needs to change')
    return p
  }
  if (!has(d.project.name)) p.push('Add the project name')
  if (!has(d.project.application)) p.push('Pick the application')
  if (!d.isQuick) {
    if (!has(d.project.siteSuburb) && !has(d.project.siteAddress))
      p.push('Add at least a suburb or city')
  }
  if (d.project.urgency && !has(d.project.urgencyReason)) p.push('Say why it is urgent')
  return p
}
